import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import Grid from '@mui/material/Grid';
import { Divider, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import { useTheme } from '@mui/material/styles';
import MainCard from '../MainCard';
import { ApartmentOutlined } from '@ant-design/icons';
import { useSelector } from 'store';
import { NavItemType } from 'types/menu';
import { OverrideIcon, RootStateProps } from 'types/root';
import { getMenuIntlId } from 'utils/menuTranslation';

function isSafeHref(href?: string): href is string {
  if (!href) return false;
  return !/(^|\/)(undefined|null)(\/|$|[?#])/i.test(href);
}

function translateMenuTitle(intl: ReturnType<typeof useIntl>, item: NavItemType): string {
  const intlId = getMenuIntlId(item);
  if (!intlId) return String(item.title ?? '');
  return intl.formatMessage({ id: intlId, defaultMessage: String(item.title ?? '') });
}

// ==============================|| BREADCRUMBS ||============================== //

export interface BreadCrumbSxProps extends CSSProperties {
  mb?: string;
  bgcolor?: string;
}

interface Props {
  card?: boolean;
  divider?: boolean;
  icon?: boolean;
  icons?: boolean;
  maxItems?: number;
  navigation?: { items: NavItemType[] };
  rightAlign?: boolean;
  separator?: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadCrumbSxProps;
}

const Item = (p: NavItemType) => {
  if (isSafeHref(p.url)) {
    return (
      <Link href={p.url} passHref prefetch={false} style={{ textDecoration: 'none' }}>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            textDecoration: 'none !important',
            transition: 'color 0.2s ease',
            '&:hover': { color: 'primary.main', cursor: 'pointer' }
          }}
        >
          {p.title}
        </Typography>
      </Link>
    );
  }

  return (
    <Typography variant="body1" color="text.primary" fontWeight={500}>
      {p.title}
    </Typography>
  );
};
const Breadcrumbs = ({
  card,
  divider = true,
  icon,
  icons,
  maxItems,
  navigation,
  rightAlign,
  separator,
  title,
  titleBottom,
  sx,
  ...others
}: Props) => {
  const theme = useTheme();
  const router = useRouter();
  const intl = useIntl();
  const breadcrumb = useSelector((state: RootStateProps) => state.breadcrumb);
  const [main, setMain] = useState<NavItemType | undefined>();
  const [item, setItem] = useState<NavItemType>();
  const [subitems, setSubItems] = useState<NavItemType[]>();

  const iconSX = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  const currentPath = useMemo(() => {
    let p = (router.asPath || router.pathname || '').split('?')[0] || '';
    if (typeof window !== 'undefined') {
      p = window.location.pathname;
    }
    if (p === '/components-overview/breadcrumbs') {
      p = '/dashboard/analytics';
    }
    if (p === '/apps/kanban/backlogs') {
      p = '/apps/kanban/board';
    }
    return p;
  }, [router.asPath, router.pathname]);

  const [breadcrumbLinkBase, setBreadcrumbLinkBase] = useState('');

  // set active item state
  const getCollapse = (menu: NavItemType) => {
    if (menu.children) {
      menu.children.filter((collapse: NavItemType) => {
        if (collapse.type && collapse.type === 'collapse') {
          getCollapse(collapse as { children: NavItemType[]; type?: string });
        } else if (collapse.type && collapse.type === 'item' && collapse.breadcrumbs !== false) {
          if (currentPath.includes(collapse.url!)) {
            setMain(menu);
            setItem(collapse);
          }
        }
        return false;
      });
    }
  };

  useEffect(() => {
    if (breadcrumb.show) {
      return;
    }
    setSubItems([]);
    navigation?.items?.forEach((menu: NavItemType) => {
      if (menu.type && menu.type === 'group') {
        getCollapse(menu as { children: NavItemType[]; type?: string });
      }
    });
  }, [currentPath, navigation?.items, breadcrumb.show]);

  useEffect(() => {
    if (!breadcrumb.show) {
      setBreadcrumbLinkBase('');
      return;
    }
    setMain(undefined);
    setItem(undefined);
    const subs: NavItemType[] = [];
    let linkBase = '';
    breadcrumb.items.forEach((bcItem) => {
      if (bcItem.type === 'collapse') {
        setMain(bcItem);
        linkBase = isSafeHref(bcItem?.url) ? bcItem.url : '';
      } else if (bcItem.type === 'item') {
        setItem(bcItem);
      } else {
        subs.push(bcItem);
      }
    });
    setBreadcrumbLinkBase(linkBase);
    setSubItems(subs);
  }, [breadcrumb.show, breadcrumb.items]);

  const linkPathForHeader = breadcrumb.show ? (isSafeHref(breadcrumbLinkBase) ? breadcrumbLinkBase : currentPath) : currentPath;

  // item separator
  const SeparatorIcon = separator!;
  const separatorIcon = separator ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} /> : '/';

  let mainContent;
  let itemContent;
  let breadcrumbContent: ReactElement = <Typography />;
  let itemTitle: NavItemType['title'] = '';
  let CollapseIcon;
  let ItemIcon;

  // collapse item
  if (main && main.type === 'collapse') {
    CollapseIcon = main.icon ? main.icon : ApartmentOutlined;
    mainContent = (
      <Link
        href={breadcrumb.show ? (isSafeHref(main.url) ? main.url : linkPathForHeader) : currentPath}
        passHref
        prefetch={false}
        style={{ textDecoration: 'none' }}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            textDecoration: 'none !important',
            transition: 'color 0.2s ease',
            '&:hover': { color: 'primary.main', cursor: 'pointer' }
          }}
        >
          {icons && <CollapseIcon style={iconSX} />}
          {translateMenuTitle(intl, main)}
        </Typography>
      </Link>
    );
  }

  // items
  if (item && item.type === 'item' && item.breadcrumbs !== false) {
    itemTitle = translateMenuTitle(intl, item);

    ItemIcon = item.icon ? item.icon : ApartmentOutlined;
    itemContent = (
      <Link
        href={breadcrumb.show ? (isSafeHref(item.url) ? item.url : linkPathForHeader) : currentPath}
        passHref
        prefetch={false}
        style={{ textDecoration: 'none' }}
      >
        <Typography
          variant="body1"
          sx={{
            textDecoration: 'none !important',
            fontWeight: subitems && subitems.length > 0 ? 400 : 500,
            transition: 'color 0.2s ease',
            '&:hover': { color: 'primary.main', cursor: 'pointer' }
          }}
          color={subitems && subitems.length > 0 ? 'text.secondary' : 'text.primary'}
        >
          {icons && <ItemIcon style={iconSX} />}
          {itemTitle}
        </Typography>
      </Link>
    );

    // main
    breadcrumbContent = (
      <MainCard border={card} sx={card === false ? { bgcolor: 'transparent', ...sx } : { ...sx }} {...others} content={card} shadow="none">
        <Grid
          container
          direction={rightAlign ? 'row' : 'column'}
          justifyContent={rightAlign ? 'space-between' : 'flex-start'}
          alignItems={rightAlign ? 'center' : 'flex-start'}
          spacing={1}
        >
          {title && !titleBottom && (
            <Grid>
              <Typography variant="h3" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
                {translateMenuTitle(intl, item)}
              </Typography>
            </Grid>
          )}
          <Grid>
            <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
              {mainContent}
              {itemContent}
              {subitems &&
                subitems.map((i, index) => (
                  <Item key={`breadcrumb_item_${index}`} {...i} url={index == subitems.length - 1 ? '' : i.url} />
                ))}
            </MuiBreadcrumbs>
          </Grid>
          {/* {title && titleBottom && (
            <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
              <Typography variant="h2">{item.title}</Typography>
            </Grid>
          )} */}
        </Grid>
        {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
      </MainCard>
    );
  }

  return breadcrumbContent;
};

export default Breadcrumbs;
